// src/pages/ProjectsAll/ProjectsAll.js
import React, { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./ProjectsAll.css";

/* ✅ Match ProjectsStrip logic + style (single source of truth) */
const GCS_BASE = "https://storage.googleapis.com/my-portfolio-media";

function isVideoSrc(src) {
  const s = (src || "").toLowerCase();
  return s.endsWith(".mp4") || s.endsWith(".webm") || s.endsWith(".mov");
}

function resolveGcsMedia(media) {
  if (!media) return "";
  if (/^https?:\/\//i.test(media)) return media;
  const clean = String(media).replace(/^\/+/, "");
  return `${GCS_BASE}/${clean}`;
}

export default function ProjectsAll() {
  const videoRefs = useRef({});
  const navigate = useNavigate();

  const projects = useMemo(
    () => [
      {
        key: "hydromind",
        title: "HydroMind",
        subtitle: "Automated hydroponics control system",
        tag: "Smart Automation",
        audience: "Home growers & small farms",
        desc:
          "A control dashboard that monitors sensors and automates dosing/pumps so plants stay healthy with minimal manual work.",
        highlights: [
          "Live sensor monitoring (pH, nutrients, temperature)",
          "Automated dosing schedules + safety checks",
          "Hardware + software built as a complete product",
        ],
        stack: ["React", "Flask", "MySQL", "Cloud Run", "Cloud SQL", "KiCad"],
        media: `${GCS_BASE}/hydromind.mp4`,
      },
      {
        key: "sahar-watches",
        title: "Sahar Watches",
        subtitle: "Luxury watch showroom & operations platform",
        tag: "E-commerce Ops",
        audience: "Watch dealer - Private Customer",
        desc:
          "A platform to manage inventory, customers, and sales workflows for a premium watch dealership.",
        highlights: [
          "Inventory + customer management",
          "Sales workflows & client follow-ups",
          "Images and media organized in the cloud",
        ],
        stack: ["React", "Flask", "MySQL", "Google Cloud Storage"],
        media: `${GCS_BASE}/sahar-watches.mp4`,
      },
      {
        key: "ubic",
        title: "Construction Contractor",
        subtitle: "Lead-generation website for a contractor",
        tag: "Business Website",
        audience: "Local service business - Private Customer",
        desc:
          "A clean website that builds trust, showcases work, and makes it easy for customers to get in touch.",
        highlights: [
          "Service pages + project gallery",
          "Fast loading, mobile-friendly layout",
          "Simple contact flow for leads",
        ],
        stack: ["React", "Apps Script", "Google Cloud Storage"],
        media: `${GCS_BASE}/ubic.mp4`,
      },
      {
        key: "needle",
        title: "Needle",
        subtitle: "Inventory & sales system for watch dealers",
        tag: "Internal Tool",
        audience: "Watch Dealers / back office",
        desc:
          "A streamlined system that tracks purchases, sales, and watch photos to keep operations organized.",
        highlights: [
          "Watch albums and detailed item pages",
          "Purchases & sales history tracking",
          "Import/export with clean templates",
        ],
        stack: ["React", "Flask", "MySQL", "Cloud Run", "Cloud SQL", "GCS"],
        media: `${GCS_BASE}/needle.mp4`,
      },
      {
        key: "inspection",
        title: "Inspection Platform",
        subtitle: "Photo-to-report workflow for inspectors",
        tag: "Internal Tool",
        audience: "Inspection company - Private Customer",
        desc:
          "An internal tool that helps inspectors upload photos, apply standardized notes, and generate professional reports.",
        highlights: [
          "Structured photo upload & labeling flow",
          "Approval-ready reporting workflow",
          "PDF generation stored in cloud storage",
        ],
        stack: ["React", "Flask", "MySQL", "Cloud Run", "Cloud SQL", "GCS"],
        media: `${GCS_BASE}/inspections.mp4`,
      },
      {
        key: "asx",
        title: "All Star Xperience",
        subtitle: "Soccer league website & stats hub",
        tag: "Sports Platform",
        audience: "Players, teams, fans - Private Customer",
        desc:
          "A league platform that shows schedules, standings, and top scorers with a clean, modern experience.",
        highlights: [
          "Standings, schedules, and leaderboards",
          "Simple admin-friendly data updates",
          "Mobile-first UI for players",
        ],
        stack: ["React", "Apps Script"],
        media: `${GCS_BASE}/asx.mp4`,
      },
    ],
    []
  );

  useEffect(() => {
    const keys = projects.map((p) => p.key);

    const warm = async () => {
      for (const key of keys) {
        const v = videoRefs.current[key];
        if (!v) continue;

        try {
          v.muted = true;
          v.playsInline = true;
          v.preload = "auto";
          v.load?.();

          const playPromise = v.play?.();
          if (playPromise?.catch) await playPromise.catch(() => {});

          await new Promise((r) => setTimeout(r, 450));
          v.pause?.();

          try {
            v.currentTime = 0;
          } catch {}
        } catch {}
      }
    };

    const t = setTimeout(warm, 120);
    return () => clearTimeout(t);
  }, [projects]);

  const onEnter = (key) => {
    const v = videoRefs.current[key];
    if (!v) return;

    try {
      v.currentTime = 0;
    } catch {}

    try {
      const p = v.play?.();
      if (p?.catch) p.catch(() => {});
    } catch {}
  };

  const onLeave = (key) => {
    const v = videoRefs.current[key];
    if (!v) return;

    try {
      v.pause?.();
    } catch {}

    try {
      v.currentTime = 0;
    } catch {}
  };

  // ✅ row navigation helpers
  const goToProject = (key) => navigate(`/projects/${key}`);

  const isInteractiveTarget = (e) => {
    // ignore clicks on links/buttons/inputs inside the row
    return !!e.target.closest("a, button, input, textarea, select, label");
  };

  return (
    <main className="pall" aria-label="All projects">
      <div className="pall__shell">
        <header className="pall__head">
          <div className="pall__headGrid">
            <div className="pall__headLeft">
              <div className="pall__kicker">Selected Work</div>
              <h1 className="pall__title">Some of my work</h1>
              <p className="pall__sub">
                A quick overview of some products, dashboards, and internal tools I’ve built.
              </p>
            </div>

            <div className="pall__headRight">
              <Link className="pall__back" to="/">
                ← Back
              </Link>
            </div>
          </div>
        </header>

        <section className="pall__list" aria-label="Projects list">
          {projects.map((p) => {
            const src = resolveGcsMedia(p.media);
            const vid = isVideoSrc(src);

            return (
              <article
                key={p.key}
                className="prow"
                tabIndex={0}
                role="link"
                aria-label={`Open project ${p.title}`}
                onClick={(e) => {
                  if (isInteractiveTarget(e)) return;
                  goToProject(p.key);
                  window.scrollTo(0, 0)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    goToProject(p.key);
                  }
                }}
                onMouseEnter={() => vid && onEnter(p.key)}
                onMouseLeave={() => vid && onLeave(p.key)}
                onFocus={() => vid && onEnter(p.key)}
                onBlur={() => vid && onLeave(p.key)}
              >
                <div className="prow__media" aria-label={`${p.title} preview`}>
                  {vid ? (
                    <video
                      ref={(el) => {
                        if (el) videoRefs.current[p.key] = el;
                      }}
                      className="prow__video"
                      src={src}
                      muted
                      loop
                      playsInline
                      preload="auto"
                    />
                  ) : (
                    <img className="prow__img" src={src} alt={p.title} loading="lazy" />
                  )}
                </div>

                <div className="prow__meta">
                  <div className="prow__top">
                    <div className="prow__titleRow">
                      <div>
                        <div className="prow__title">{p.title}</div>
                        <div className="prow__subtitle">{p.subtitle}</div>
                      </div>
                    </div>
                  </div>

                  <p className="prow__desc">{p.desc}</p>

                  <div className="prow__stack" aria-label={`${p.title} tech stack`}>
                    {p.stack.map((s) => (
                      <span key={s} className="prow__chip">
                        {s}
                      </span>
                    ))}
                  </div>

                  <div className="prow__openRow">
                    <Link className="prow__open" to={`/projects/${p.key}`}  onClick={() => window.scrollTo(0, 0)}>
                      Open project →
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      </div>
    </main>
  );
}
